import { useState, useEffect } from "react";

interface HackerWatchProps {
  videoUrl: string;
}

function getYouTubeId(url: string): string | null {
  const patterns = [
    /youtube\.com\/watch\?v=([^&]+)/,
    /youtube\.com\/embed\/([^?]+)/,
    /youtu\.be\/([^?]+)/,
    /youtube\.com\/shorts\/([^?]+)/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
}

export default function HackerWatch({ videoUrl }: HackerWatchProps) {
  const [open, setOpen] = useState(false);
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const iv = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(iv);
  }, []);

  const ts = time.toLocaleString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

  const videoId = videoUrl ? getYouTubeId(videoUrl) : null;
  const isLocalVideo = videoUrl && !videoId && videoUrl.startsWith("/");

  return (
    <>
      {/* Toggle button — bottom-left corner */}
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 left-6 z-50 flex items-center gap-2 rounded-lg border border-ds-border bg-ds-card/90 px-3 py-2 text-xs text-ds-text-dim backdrop-blur-md transition-all hover:border-ds-accent/40 hover:text-ds-text"
        title="Hacker Watch"
      >
        <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="1" y="3" width="14" height="10" rx="1" />
          <circle cx="8" cy="8" r="2.5" />
          <circle cx="8" cy="8" r="0.8" fill="currentColor" />
        </svg>
        <span>{open ? "Close" : "Watch"}</span>
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-ds-danger" />
      </button>

      {/* Slide-in panel */}
      <div
        className={`fixed bottom-16 left-6 z-50 w-[340px] transition-all duration-300 ${
          open ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0 pointer-events-none"
        }`}
      >
        <div className="overflow-hidden rounded-xl border border-ds-border/60 bg-ds-card shadow-2xl shadow-black/50">
          {/* CCTV Header */}
          <div className="flex items-center justify-between border-b border-ds-border/40 bg-ds-surface/80 px-3 py-2">
            <div className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-ds-danger" />
              <span className="font-mono text-[9px] uppercase tracking-widest text-ds-danger/80">REC</span>
            </div>
            <span className="font-mono text-[9px] text-ds-text-muted/50">CAM-01 // HACKER</span>
            <span className="font-mono text-[9px] text-ds-text-muted/40">{ts}</span>
          </div>

          {/* Video area */}
          <div className="relative aspect-video bg-black">
            {videoId ? (
              <iframe
                src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&modestbranding=1&rel=0`}
                className="h-full w-full"
                allow="autoplay; encrypted-media"
                allowFullScreen
                title="Surveillance feed"
              />
            ) : isLocalVideo ? (
              <video
                src={videoUrl}
                autoPlay
                muted
                loop
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="flex h-full items-center justify-center">
                <span className="font-mono text-xs text-ds-text-muted/40">NO SIGNAL</span>
              </div>
            )}

            {/* Scan lines over video */}
            <div className="surveillance-scanline absolute inset-0" />

            {/* Corner brackets inside video */}
            <div className="absolute left-2 top-2 h-5 w-5 border-l border-t border-ds-accent/25" />
            <div className="absolute right-2 top-2 h-5 w-5 border-r border-t border-ds-accent/25" />
            <div className="absolute bottom-2 left-2 h-5 w-5 border-b border-l border-ds-accent/25" />
            <div className="absolute bottom-2 right-2 h-5 w-5 border-b border-r border-ds-accent/25" />

            {/* Crosshair */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
              <div className="h-px w-6 bg-ds-accent/15" />
              <div className="absolute left-1/2 top-1/2 h-6 w-px -translate-x-1/2 -translate-y-1/2 bg-ds-accent/15" />
            </div>
          </div>

          {/* CCTV Footer */}
          <div className="flex items-center justify-between border-t border-ds-border/40 bg-ds-surface/80 px-3 py-1.5">
            <span className="font-mono text-[8px] text-ds-text-muted/30">SUBJECT: UNKNOWN</span>
            <span className="font-mono text-[8px] text-ds-success/50">STREAM ACTIVE</span>
          </div>
        </div>
      </div>
    </>
  );
}
